import React, { useState, useMemo, useRef } from 'react';
import { message, Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { t } from '../../../i18n';
import AccountsFilter, { AccountsFilterState } from "./components/AccountsFilter";
import { ApolloError, useMutation, useQuery, useApolloClient } from '@apollo/client';
import { AccountDeliveryDriverType } from './types/AccountDeliveryDriverType';
import { AccountDeliveryDriverTable } from './components/AccountDeliveryDriverTable';
import { GetDspForDspManagerResponse, GET_DSP_FOR_DSP_MANAGER } from 'graphql/queries/getDspForDspManager';
import { useVehicleTypeOptions } from 'hooks/useVehicleTypeOptions';
import { CREATE_ACCOUNT_DELIVERY_DRIVER_FOR_PORTAL, CREATE_ACCOUNT_DELIVERY_DRIVER_FOR_CENTRAL, CREATE_ACCOUNT_DELIVERY_DRIVER_FOR_WMS, CreateAccountDeliveryDriverForPortalResponse, CreateAccountDeliveryDriverForPortalVariables } from './graphql/mutations/createAccountDeliveryDriverForPortal';
import { UPDATE_ACCOUNT_DELIVERY_DRIVER, UPDATE_ACCOUNT_DELIVERY_DRIVER_FOR_CENTRAL, UPDATE_ACCOUNT_DELIVERY_DRIVER_FOR_WMS } from './graphql/mutations/updateAccountDeliveryDriver';
import { UpdateAccountDeliveryDriverResponse, UpdateAccountDeliveryDriverVariables } from './graphql/mutations/updateAccountDeliveryDriver';
import { AccountDeliveryDriverFormDrawer } from './components/AccountDeliveryDriverFormDrawer';
import { OperationLogModal } from './components/OperationLogModal';
import { showError } from 'utils/exceptionUtils';
import { DriverConflictDetails, isDriverConflictMessageKey } from 'utils/driverConflictMessage';
import DriverConflictModal, { DriverConflict } from './components/DriverConflictModal';
import { useAllDspsForPermission } from './graphql/queries/getAllDspsForPermission';
import { useAllDspsForWmsPermission } from './graphql/queries/getAllDspsForWmsPermission';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import AccountDeliveryDriverBatchCreateModal from './components/AccountDeliveryDriverBatchCreateModal';
import DriverBlacklistModal from './components/DriverBlacklistModal';
import DriverBlacklistListModal from './components/DriverBlacklistListModal';
import { downloadDeliveryDriverTemplate } from './graphql/queries/getAccountDeliveryDriverExcel';
import { AccountDeliveryDriverFiltersTypeInput } from './graphql/queries/getAccountDeliveryDriverListing';
import { useAccountDeliveryDriverQuery, AccountsPageMode, ACCOUNTS_PAGE_MODE } from './hooks/useAccountDeliveryDriverQuery';
import { GENERATE_DRIVER_DOWNLOAD_REPORT, GenerateDriverDownloadReportResponse, GenerateDriverDownloadReportVariables } from '../../central/account/graphql/mutations/generateDriverDownloadReport';
import DriverDownloadModal from '../../central/account/components/DriverDownloadModal';
import { Trans, useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { GET_LATEST_WAREHOUSE_ID_BY_DRIVER_ACCOUNT_ID_AND_DSP_ID } from '../tasks/graphql/queries/getLatestWarehouseIdByDriverAccountIdAndDspId';
import {hasResource} from "../../../utils/resourceAuth";
import {ResourceCode} from "../../../constants/resourceCodes";

interface PageBodyProps {
    mode?: AccountsPageMode;
}

const PageBody: React.FC<PageBodyProps> = ({ mode }) => {
    const { i18n } = useTranslation();
    const client = useApolloClient();
    const navigate = useNavigate();
    // 抽屉状态
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedAccountDeliveryDriver, setSelectedAccountDeliveryDriver] = useState<AccountDeliveryDriverType | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [batchUploadVisible, setBatchUploadVisible] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [operationLogVisible, setOperationLogVisible] = useState(false);
    const [operationLogAccountId, setOperationLogAccountId] = useState<number | null>(null);
    const [banDriverVisible, setBanDriverVisible] = useState(false);
    const [blacklistListVisible, setBlacklistListVisible] = useState(false);

    // 强校验弹窗状态
    const [strongValidationModalVisible, setStrongValidationModalVisible] = useState(false);
    const [strongValidationMessageKeys, setStrongValidationMessageKeys] = useState<string[]>([]);
    
    // 弱校验弹窗状态（二次确认）
    const [weakValidationModalVisible, setWeakValidationModalVisible] = useState(false);
    const [weakValidationMessageKeys, setWeakValidationMessageKeys] = useState<string[]>([]);
    const [weakValidationOnConfirm, setWeakValidationOnConfirm] = useState<(() => void) | null>(null);
    
    // 离职确认弹窗状态
    const [resignConfirmVisible, setResignConfirmVisible] = useState(false);
    const [resignConfirmRecord, setResignConfirmRecord] = useState<AccountDeliveryDriverType | null>(null);

    // 手机号 / 证件号唯一性冲突弹窗
    const [driverConflict, setDriverConflict] = useState<DriverConflict | null>(null);
    
    // 使用 ref 保存当前更新的 accountId（用于弱校验确认回调）
    const currentUpdateAccountIdRef = useRef<number | null>(null);
    
    const initialFilters: AccountsFilterState = {
        name: '',
        phone: '',
        vehiclePlate: '',
        dspId: '',
        verificationStatus: undefined,
        idDocumentNumber: '',
        isBlacklist: false,
        isEmployed: undefined
    };
    
    const [tempFilters, setTempFilters] = useState<AccountsFilterState>(initialFilters);
    const [filters, setFilters] = useState<AccountsFilterState>(initialFilters);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

    const isCentral = mode === ACCOUNTS_PAGE_MODE.CENTRAL;
    const isDsp = mode === ACCOUNTS_PAGE_MODE.DSP;
    const isWms = mode === ACCOUNTS_PAGE_MODE.WMS;
    // 黑名单按入口区分：总部看派送司机资源码，仓库看仓库黑名单资源码，DSP 入口不提供
    const showBlacklistButton = (isCentral && hasResource([ResourceCode.menu.TRANSPORT_DELIVERY_DRIVER]))
        || (isWms && hasResource([ResourceCode.menu.WHS_DELIVERY_DRIVER_BLACKLIST]));
    const showDspSelector = isCentral || isWms;

    // 获取当前仓库ID
    const { currentWarehouse } = useWarehouseContext();
    const currentWarehouseId = isDsp ? null : currentWarehouse?.id || null;

    const {
        allDspsForPermission: centralDspsData,
        loading: centralDspsLoading,
        refetch: refetchCentralDsps
    } = useAllDspsForPermission(!isCentral);

    const {
        allDspsForWmsPermission: wmsDspsData,
        loading: wmsDspsLoading,
        refetch: refetchWmsDsps
    } = useAllDspsForWmsPermission(currentWarehouseId, !isWms);

    const dspsLoading = isWms ? wmsDspsLoading : isDsp ? false : centralDspsLoading;
    const refetchDsps = isWms ? refetchWmsDsps : refetchCentralDsps;

    const dspsData = useMemo(
        () => isWms ? wmsDspsData : isDsp ? [] : isCentral ? centralDspsData : [],
        [isWms, wmsDspsData, isDsp, isCentral, centralDspsData]
    );

    // DSP 选项处理 - 所有角色都显示 Company 下拉框
    const dspOptions = useMemo(() => {
        return (dspsData || [])
            .filter(dsp => !!dsp && !!dsp.id)
            .map(dsp => ({
                value: dsp.id!,  // 保持为 string 类型
                label: dsp.name,
                dspCode: dsp.dspCode
            }));
    }, [dspsData]);


    // 获取车型数据 - 所有角色都需要
    const vehicleTypeOptions = useVehicleTypeOptions();

    // 构建查询过滤器
    const queryFilters = useMemo(() => ({
        name: filters.name || null,
        phone: filters.phone || null,
        vehiclePlate: filters.vehiclePlate || null,
        dspId: filters.dspId ? parseInt(filters.dspId) : null,
        warehouseId: currentWarehouseId ? parseInt(currentWarehouseId) : null,
        verificationStatus: filters.verificationStatus !== undefined ? filters.verificationStatus : null,
        idDocumentNumber: filters.idDocumentNumber || null,
        isBlacklist: filters.isBlacklist !== undefined ? filters.isBlacklist : null,
        isEmployed: filters.isEmployed !== undefined ? filters.isEmployed : null
    }), [filters, currentWarehouseId]);

    const {
        accountDeliveryDrivers,
        loading: roleLoading,
        refetch: refetchRole,
        totalElements
    } = useAccountDeliveryDriverQuery(queryFilters, pagination.current, pagination.pageSize, mode);
    const [generateReportMutation] = useMutation<GenerateDriverDownloadReportResponse, GenerateDriverDownloadReportVariables>(GENERATE_DRIVER_DOWNLOAD_REPORT);

    // 手机号 / 证件号唯一性冲突：弹窗展示冲突对象所在位置，其他错误返回 false 交由 showError 处理
    const handleDriverConflictError = (error: unknown): boolean => {
        const graphQLError = error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined;
        const messageKey = graphQLError?.extensions?.messageKey as string | undefined;
        if (!messageKey || !isDriverConflictMessageKey(messageKey)) {
            return false;
        }
        setDriverConflict({ messageKey, details: graphQLError?.extensions?.details as DriverConflictDetails | undefined });
        return true;
    };

    // 按入口各声明一个新建 mutation，mode 不是三种入口之一时不调用（与列表查询一致，不设默认入口）
    const createMutationOptions = {
        onCompleted: () => {
            message.success(t('delivery.personnel.message.createSuccess'));
            setDrawerVisible(false);
            handleRefetch();
        },
        onError: (error: ApolloError) => {
            if (!handleDriverConflictError(error)) {
                showError(error);
            }
        }
    };
    const [createForCentral] = useMutation<CreateAccountDeliveryDriverForPortalResponse, CreateAccountDeliveryDriverForPortalVariables>(
        CREATE_ACCOUNT_DELIVERY_DRIVER_FOR_CENTRAL, createMutationOptions);
    const [createForWms] = useMutation<CreateAccountDeliveryDriverForPortalResponse, CreateAccountDeliveryDriverForPortalVariables>(
        CREATE_ACCOUNT_DELIVERY_DRIVER_FOR_WMS, createMutationOptions);
    const [createForDsp] = useMutation<CreateAccountDeliveryDriverForPortalResponse, CreateAccountDeliveryDriverForPortalVariables>(
        CREATE_ACCOUNT_DELIVERY_DRIVER_FOR_PORTAL, createMutationOptions);
    const createAccountDeliveryDriver = mode
        ? { [ACCOUNTS_PAGE_MODE.CENTRAL]: createForCentral, [ACCOUNTS_PAGE_MODE.WMS]: createForWms, [ACCOUNTS_PAGE_MODE.DSP]: createForDsp }[mode]
        : undefined;

    // 显示强校验弹窗
    const showStrongValidationModal = (driverTaskStatuses: string[]) => {
        if (driverTaskStatuses.length === 0) {
            return;
        }
        
        setStrongValidationMessageKeys(driverTaskStatuses);
        setStrongValidationModalVisible(true);
    };

    // 处理强校验弹窗确认
    const handleStrongValidationModalOk = () => {
        setStrongValidationModalVisible(false);
        setStrongValidationMessageKeys([]);
    };

    // 处理强校验弹窗取消
    const handleStrongValidationModalCancel = () => {
        setStrongValidationModalVisible(false);
        setStrongValidationMessageKeys([]);
    };

    // 显示弱校验弹窗（二次确认）
    const showWeakValidationModal = (
        messageKeys: string[],
        onConfirm?: () => void
    ) => {
        if (messageKeys.length === 0) {
            return;
        }
        setWeakValidationMessageKeys(messageKeys);
        setWeakValidationOnConfirm(() => onConfirm || null);
        setWeakValidationModalVisible(true);
    };

    // 处理弱校验弹窗确认
    const handleWeakValidationModalOk = () => {
        setWeakValidationModalVisible(false);
        if (weakValidationOnConfirm) {
            weakValidationOnConfirm();
        }
        setWeakValidationMessageKeys([]);
        setWeakValidationOnConfirm(null);
    };

    // 处理弱校验弹窗取消
    const handleWeakValidationModalCancel = () => {
        setWeakValidationModalVisible(false);
        setWeakValidationMessageKeys([]);
        setWeakValidationOnConfirm(null);
    };

    // 显示离职确认弹窗
    const showResignConfirmModal = (record: AccountDeliveryDriverType) => {
        setResignConfirmRecord(record);
        setResignConfirmVisible(true);
    };

    // 处理离职确认弹窗确认
    const handleResignConfirmOk = async () => {
        if (resignConfirmRecord) {
            await handleResignDriver(resignConfirmRecord);
            setResignConfirmVisible(false);
            setResignConfirmRecord(null);
        }
    };

    // 处理离职确认弹窗取消
    const handleResignConfirmCancel = () => {
        setResignConfirmVisible(false);
        setResignConfirmRecord(null);
    };

    const buildTaskLink = (index: number) => {
        const taskPath = isDsp ? '/delivery/tasks' : '/wms/tasks';
        
        // 获取当前编辑的司机信息
        const driver = selectedAccountDeliveryDriver;
        
        // DSP角色是两级菜单，其他角色是三级菜单
        const linkParts: (string | JSX.Element)[] = [];
        if (isDsp) {
            linkParts.push(t('delivery.title'));
            linkParts.push('-');
            linkParts.push(t('delivery.menu.tasks'));
        } else {
            linkParts.push(t('wms.title'));
            linkParts.push('-');
            linkParts.push(t('delivery.title'));
            linkParts.push('-');
            linkParts.push(t('delivery.menu.tasks'));
        }
        
        // dsp 入口的司机离职逻辑保持不变（恒可访问）；非 dsp 场景按 WHS_DELIVERY_TASK 资源码判断（否则跳转后台查询会报无权限）
        if (!(isDsp || hasResource([ResourceCode.menu.WHS_DELIVERY_TASK]))) {
            return (
                <>
                    {' '}
                    <span key={`task-${index}`}>{linkParts}</span>
                    {' '}
                    {t('common.inquire')}
                </>
            );
        }

        return (
            <>
                {' '}
                <a
                    key={`task-${index}`}
                    onClick={async (e) => {
                        e.preventDefault();
                        // 如果没有司机信息，直接跳转不带参数
                        if (!driver || !driver.accountId || !driver.dspId) {
                            navigate(taskPath);
                            return;
                        }
                        
                        try {
                            // 先查询最新的warehouseId
                            const { data } = await client.query({
                                query: GET_LATEST_WAREHOUSE_ID_BY_DRIVER_ACCOUNT_ID_AND_DSP_ID,
                                variables: {
                                    driverAccountId: driver.accountId,
                                    dspId: driver.dspId
                                },
                                fetchPolicy: 'network-only'
                            });
                            
                            const warehouseId = data?.getLatestWarehouseIdByDriverAccountIdAndDspId;
                            
                            // 构建URL参数
                            const params = new URLSearchParams();
                            if (warehouseId && !isDsp) {
                                // 非DSP角色需要传递warehouseId
                                params.set('warehouseId', String(warehouseId));
                            }
                            if (driver.accountId) {
                                params.set('driverAccountId', String(driver.accountId));
                            }
                            // 默认选中"新建"和"派送中"状态
                            params.set('status', 'CREATED,DELIVERING');
                            
                            const taskPathWithParams = `${taskPath}?${params.toString()}`;
                            navigate(taskPathWithParams);
                        } catch (error) {
                            console.error('查询warehouseId失败:', error);
                            // 如果查询失败，仍然跳转，但不带warehouseId参数
                            const params = new URLSearchParams();
                            if (driver.accountId) {
                                params.set('driverAccountId', String(driver.accountId));
                            }
                            params.set('status', 'CREATED,DELIVERING');
                            const taskPathWithParams = `${taskPath}?${params.toString()}`;
                            navigate(taskPathWithParams);
                        }
                    }}
                    style={{ color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
                >
                    {linkParts}
                </a>
                {' '}
                {t('common.inquire')}
            </>
        );
    };

    // 获取所有强校验消息列表（后端直接返回翻译键，无需映射）
    const getAllStrongValidationMessages = () => {
        // 定义需要拼接跳转链接的翻译键（只有这三个强校验需要链接）
        const taskLinkKeys = [
            'delivery.personnel.message.resignation.hasRoutedUnpickedParcels',
            'delivery.personnel.message.resignation.hasPickedUpUndeliveredParcels',
            'delivery.personnel.message.resignation.hasTransferredUnpickedParcels',
        ];
        
        const hasMultipleMessages = strongValidationMessageKeys.length > 1;
        
        return strongValidationMessageKeys.map((translationKey, index) => {
            const messagePrefix = t(translationKey);
            
            // 判断是否需要添加任务链接
            const needsTaskLink = taskLinkKeys.includes(translationKey);
            
            return {
                index: hasMultipleMessages ? index + 1 : null,
                content: (
                    <>
                        {messagePrefix}
                        {needsTaskLink && buildTaskLink(index)}
                    </>
                )
            };
        });
    };

    // 仓库入口需额外携带当前仓库ID
    const updateScopeVariables = isWms && currentWarehouseId ? { warehouseId: parseInt(currentWarehouseId) } : {};

    // 按入口各声明一个更新 mutation（三个文档通过别名返回相同的响应字段），mode 不是三种入口之一时不调用
    const updateMutationOptions = {
        onCompleted: (data: UpdateAccountDeliveryDriverResponse) => {
            const response = data.updateAccountDeliveryDriver;

            // 如果成功
            if (response.success) {
                message.success(t('delivery.personnel.message.updateSuccess'));
                setDrawerVisible(false);
                handleRefetch();
                return;
            }

            // 如果需要二次确认（弱校验）
            if (response.requiresConfirmation) {
                // 使用闭包捕获 originalInput 和 accountId（从第一次调用的 variables 中获取）
                const originalInput = response.originalInput;
                const accountId = currentUpdateAccountIdRef.current;

                // 显示弱校验弹窗，确认后重新提交
                showWeakValidationModal(
                    response.driverTaskStatuses,
                    async () => {
                        // 确认后，重新提交：原封不动传回 originalInput，删除 __typename 字段
                        const { __typename, ...inputWithoutTypename } = originalInput as any;
                        await updateAccountDeliveryDriver?.({
                            variables: {
                                accountId: accountId!,
                                input: inputWithoutTypename,
                                confirmed: true,
                                ...updateScopeVariables,
                            },
                        });
                    }
                );
                return;
            }

            // 如果有司机任务状态（不可以离职的情况）
            if (response.driverTaskStatuses && response.driverTaskStatuses.length > 0) {
                // 显示强校验弹窗
                showStrongValidationModal(response.driverTaskStatuses);
                return;
            }
        },
        onError: (error: ApolloError) => {
            if (!handleDriverConflictError(error)) {
                showError(error);
            }
        }
    };
    const [updateForCentral] = useMutation<UpdateAccountDeliveryDriverResponse, UpdateAccountDeliveryDriverVariables>(
        UPDATE_ACCOUNT_DELIVERY_DRIVER_FOR_CENTRAL, updateMutationOptions);
    const [updateForWms] = useMutation<UpdateAccountDeliveryDriverResponse, UpdateAccountDeliveryDriverVariables>(
        UPDATE_ACCOUNT_DELIVERY_DRIVER_FOR_WMS, updateMutationOptions);
    const [updateForDsp] = useMutation<UpdateAccountDeliveryDriverResponse, UpdateAccountDeliveryDriverVariables>(
        UPDATE_ACCOUNT_DELIVERY_DRIVER, updateMutationOptions);
    const updateAccountDeliveryDriver = mode
        ? { [ACCOUNTS_PAGE_MODE.CENTRAL]: updateForCentral, [ACCOUNTS_PAGE_MODE.WMS]: updateForWms, [ACCOUNTS_PAGE_MODE.DSP]: updateForDsp }[mode]
        : undefined;

    // 统一loading状态
    const loading = roleLoading;

    // 统一refetch函数
    const handleRefetch = () => {
        refetchRole();
        if (showDspSelector) {
            refetchDsps();
        }
    };

    // 只有DSP Manager才查询DSP信息，其他角色跳过
    const { data: dspData } = useQuery<GetDspForDspManagerResponse>(GET_DSP_FOR_DSP_MANAGER, {
        skip: !isDsp
    });
    const dsp = dspData?.dsp;

    // 显示账号详情
    const showAccountDeliveryDriverDetail = (accountDeliveryDriver: AccountDeliveryDriverType) => {
        setSelectedAccountDeliveryDriver(accountDeliveryDriver);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // 显示操作记录
    const showOperationLog = (accountId: number) => {
        setOperationLogAccountId(accountId);
        setOperationLogVisible(true);
    };

    // 显示新建账号抽屉
    const showNewAccountDrawer = () => {
        setSelectedAccountDeliveryDriver(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    const makeAccountDeliveryDriverInput = (values: AccountDeliveryDriverType) => {
        // 根据用户角色确定dspCode
        let dspCode: string | undefined = '';

        if (showDspSelector) {
            dspCode = dspOptions.find(dsp => dsp.value === values.dspId.toString())?.dspCode;
        } else if (isDsp) {
            // DSP Manager：使用查询到的dspCode
            dspCode = dsp?.dspCode || '';
        }

        return {
            email: values.account.email,
            countryCode: values.account.countryCode,
            phone: values.account.phone,
            password: values.account.password,
            name: values.account.name,
            status: values.account.status ?? 1,
            driverLicenseNumber: values.driverLicenseNumber,
            vehicleTypeId: values.vehicleTypeId,
            vehiclePlate: values.vehiclePlate,
            vehicleImageS3KeysJson: values.vehiclePhotos || [],
            dspCode: dspCode,
            isBlacklist: values.isBlacklist ?? false,
            isOnDuty: values.isOnDuty,
            isEmployed: values.account.isEmployed,
            idDocumentType: values.idDocumentType ?? undefined,
            idDocumentNumber: values.idDocumentNumber ?? undefined,
            idDocumentImageS3KeysJson: values.idDocumentPhotos || [],
        }
    };

    const handleCreateAccountDeliveryDriver = async (values: AccountDeliveryDriverType) => {
        await createAccountDeliveryDriver?.({
            variables: {
                input: makeAccountDeliveryDriverInput(values),
                ...updateScopeVariables,
            },
        });
    };

    const handleUpdateAccountDeliveryDriver = async (values: AccountDeliveryDriverType, confirmed: boolean = false) => {
        const accountId = values.account.id!;
        // 保存 accountId 到 ref，供 onCompleted 回调使用（从第一次调用的 variables 中获取）
        currentUpdateAccountIdRef.current = accountId;
        
        await updateAccountDeliveryDriver?.({
            variables: {
                accountId: accountId,
                input: makeAccountDeliveryDriverInput(values),
                confirmed,
                ...updateScopeVariables,
            },
        });
    };

    // 处理表单提交
    const handleAccountSubmit = async (values: AccountDeliveryDriverType) => {
        if (drawerMode === 'create') {
            await handleCreateAccountDeliveryDriver(values);
        } else {
            await handleUpdateAccountDeliveryDriver(values);
        }
    };

    // 数据更新后的回调，用于刷新列表数据
    const handleDataUpdated = () => {
        handleRefetch();
    };

    // 司机离职：直接调用更新 mutation，仅修改在职状态
    const handleResignDriver = async (record: AccountDeliveryDriverType) => {
        const accountId = record.account.id!;
        currentUpdateAccountIdRef.current = accountId;

        let dspCode: string | undefined = '';
        if (showDspSelector) {
            dspCode = dspOptions.find(dsp => dsp.value === record.dspId.toString())?.dspCode;
        } else if (isDsp) {
            dspCode = dsp?.dspCode || '';
        }

        const input = {
            email: record.account.email,
            countryCode: record.account.countryCode,
            phone: record.account.phone,
            name: record.account.name,
            status: record.account.status,
            driverLicenseNumber: record.driverLicenseNumber,
            vehicleTypeId: record.vehicleTypeId,
            vehiclePlate: record.vehiclePlate,
            vehicleImageS3KeysJson: record.vehicleImageS3KeysJson || [],
            dspCode: dspCode,
            isBlacklist: record.isBlacklist ?? false,
            isOnDuty: record.isOnDuty,
            isEmployed: false,
            idDocumentType: record.idDocumentType ?? undefined,
            idDocumentNumber: record.idDocumentNumber ?? undefined,
            idDocumentImageS3KeysJson: record.idDocumentImageS3KeysJson || [],
        };

        await updateAccountDeliveryDriver?.({
            variables: { accountId, input, confirmed: false, ...updateScopeVariables },
        });
    };

    const handleTableChange = (pagination: any) => {
        // AntD Table onChange may pass empty pagination when pagination={false} (e.g. filter/sort),
        // never write undefined into state; otherwise GraphQL PageRequestTypeInput.page(Int!) may become null.
        setPagination((prev) => ({
            current: Number.isFinite(Number(pagination?.current)) ? Number(pagination.current) : (prev.current || 1),
            pageSize: Number.isFinite(Number(pagination?.pageSize)) ? Number(pagination.pageSize) : (prev.pageSize || 10),
        }));
    };

    // 批量上传成功回调
    const handleBatchUploadSuccess = () => {
        setBatchUploadVisible(false);
        handleRefetch(); // 重新获取数据
        message.success(t('delivery.personnel.message.batchUploadComplete'));
    };

    // 下载模板
    const handleDownloadTemplate = async () => {
        setDownloadLoading(true);
        try {
            await downloadDeliveryDriverTemplate();
            message.success(t('delivery.personnel.message.templateDownloadSuccess'));
        } catch (error) {
            message.error(t('delivery.personnel.message.templateDownloadFailed'));
        } finally {
            setDownloadLoading(false);
        }
    };

    // 导出司机信息（异步，下载中心模式）
    const DOWNLOAD_COOLDOWN = 5000;
    const DOWNLOAD_LAST_TIME_KEY = 'deliveryDriverDownloadLastTime';

    const handleExportDrivers = async () => {
        const currentTime = Date.now();
        const lastTime = parseInt(localStorage.getItem(DOWNLOAD_LAST_TIME_KEY) || '0', 10);

        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(t('delivery.parcels.message.downloadCooldown', { seconds: remainingTime }));
            return;
        }

        if (exportLoading) {
            message.warning(t('delivery.parcels.message.downloadInProgress'));
            return;
        }

        setExportLoading(true);
        try {
            localStorage.setItem(DOWNLOAD_LAST_TIME_KEY, currentTime.toString());

            const normalizeLanguage = (lang: string | undefined | null): string => {
                if (!lang) return 'en';
                return lang.toLowerCase().split('-')[0];
            };

            const currentLanguage = normalizeLanguage(
                i18n.resolvedLanguage || i18n.language || 'en'
            );

            const exportFilters: AccountDeliveryDriverFiltersTypeInput = {
                name: filters.name || null,
                phone: filters.phone || null,
                vehiclePlate: filters.vehiclePlate || null,
                dspId: filters.dspId ? parseInt(filters.dspId) : null,
                verificationStatus: filters.verificationStatus !== undefined ? filters.verificationStatus : null,
                idDocumentNumber: filters.idDocumentNumber || null,
                isEmployed: filters.isEmployed !== undefined ? filters.isEmployed : null
            };

            const response = await generateReportMutation({
                variables: {
                    filters: exportFilters,
                    language: currentLanguage,
                }
            });

            const fileName = response.data?.generateDriverDownloadReport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                message.error(t('delivery.driver.exportDriversFailed'));
            }
        } catch (error: any) {
            console.error('Export drivers error:', error);
            showError(error);
        } finally {
            setExportLoading(false);
        }
    };

    const handleResetFilters = () => {
        setTempFilters(initialFilters);
        setFilters(initialFilters);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const showNewButton = isDsp
        || (isCentral && hasResource([ResourceCode.menu.TRANSPORT_DELIVERY_DRIVER]))
        || (isWms && hasResource([ResourceCode.menu.WHS_DELIVERY_DRIVER]));

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 车队代码提示（司机 APP 注册时需输入）；dsp 仅在 DSP 入口查询，其他入口为空不展示 */}
            {dsp?.dspCode && (
                <div className="flex items-center gap-4 mt-3 px-4 py-3 rounded-lg bg-purple-50">
                    <span className="text-gray-500">{t('delivery.personnel.label.fleetCode')}</span>
                    <span className="px-3 py-1 rounded border border-dashed border-purple-400 font-mono text-xl font-bold tracking-widest text-purple-700">
                        {dsp.dspCode}
                    </span>
                    <span className="text-gray-700">
                        <Trans i18nKey="delivery.personnel.message.fleetCodeTip" components={{ b: <strong /> }} />
                    </span>
                </div>
            )}

            {/* 筛选条件 */}
            <AccountsFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                showDspFilter={showDspSelector}
                dspOptions={dspOptions}
                dspLoading={dspsLoading}
            />

            {/* 批量操作区域 */}
            <div className="flex justify-between items-center gap-2 mb-3 pl-0">
                <div className="flex items-center gap-2">
                    {showNewButton && (
                        <Button
                            type="primary"
                            onClick={showNewAccountDrawer}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.new')}</span>
                        </Button>
                    )}
                    <Button
                        type="primary"
                        onClick={() => setBatchUploadVisible(true)}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('delivery.personnel.label.batchUpload')}</span>
                    </Button>
                    <Button
                        type="default"
                        loading={downloadLoading}
                        onClick={handleDownloadTemplate}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('delivery.personnel.label.downloadTemplate')}</span>
                    </Button>
                    {showBlacklistButton && (
                        <Button
                            type="default"
                            onClick={() => setBanDriverVisible(true)}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('delivery.personnel.label.banDriver')}</span>
                        </Button>
                    )}
                    {showBlacklistButton && (
                        <Button
                            type="default"
                            onClick={() => setBlacklistListVisible(true)}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('delivery.personnel.label.driverBlacklist')}</span>
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {/*{isHeadquartersAdmin() && (*/}
                    {/*    <Button*/}
                    {/*        type="default"*/}
                    {/*        loading={exportLoading}*/}
                    {/*        onClick={handleExportDrivers}*/}
                    {/*        className="w-auto min-w-24"*/}
                    {/*    >*/}
                    {/*        <span className="font-bold">{t('delivery.driver.exportDrivers')}</span>*/}
                    {/*    </Button>*/}
                    {/*)}*/}
                    <Button
                        type="primary"
                        onClick={handleFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.filter')}</span>
                    </Button>
                    <Button
                        onClick={handleResetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.reset')}</span>
                    </Button>
                </div>
            </div>

            <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                <AccountDeliveryDriverTable
                    accountDeliveryDrivers={loading ? [] : accountDeliveryDrivers}
                    showAccountDeliveryDriverDetail={showAccountDeliveryDriverDetail}
                    vehicleTypeOptions={vehicleTypeOptions}
                    onDataUpdated={handleDataUpdated}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: totalElements,
                        showSizeChanger: true,
                        showQuickJumper: true,
                    }}
                    onChange={handleTableChange}
                    onShowOperationLog={showOperationLog}
                    onResignDriver={showResignConfirmModal}
                    mode={mode}
                    warehouseId={currentWarehouseId ? parseInt(currentWarehouseId) : null}
                />
            </div>

            {/* 分页 */}
            <TablePaginationBar
                total={totalElements}
                current={pagination.current}
                pageSize={pagination.pageSize}
                onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
            />

            <AccountDeliveryDriverFormDrawer
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleAccountSubmit}
                accountDeliveryDriver={selectedAccountDeliveryDriver}
                mode={drawerMode}
                source={mode}
                dspOptions={dspOptions}
                dspLoading={dspsLoading}
                onUniquenessConflict={setDriverConflict}
            />

            {/* 手机号 / 证件号唯一性冲突弹窗 */}
            <DriverConflictModal conflict={driverConflict} onClose={() => setDriverConflict(null)} />

            {/* 强校验弹窗 */}
            <Modal
                open={strongValidationModalVisible}
                title={t('common.reminder')}
                okText={t('components.button.confirm')}
                onOk={handleStrongValidationModalOk}
                onCancel={handleStrongValidationModalCancel}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <div>
                    {getAllStrongValidationMessages().map((item, index) => (
                        <p key={index} style={{ marginBottom: index < getAllStrongValidationMessages().length - 1 ? '8px' : '0' }}>
                            {item.index !== null ? `${item.index}. ` : ''}{item.content}
                        </p>
                    ))}
                </div>
            </Modal>

            {/* 弱校验弹窗（二次确认） */}
            <Modal
                open={weakValidationModalVisible}
                title={t('common.reminder')}
                okText={t('components.button.confirm')}
                cancelText={t('components.button.cancel')}
                onOk={handleWeakValidationModalOk}
                onCancel={handleWeakValidationModalCancel}
            >
                <div>
                    {weakValidationMessageKeys.map((messageKey, index) => {
                        const hasMultipleMessages = weakValidationMessageKeys.length > 1;
                        return (
                            <p key={index} style={{ marginBottom: index < weakValidationMessageKeys.length - 1 ? '8px' : '0' }}>
                                {hasMultipleMessages ? `${index + 1}. ` : ''}{t(messageKey)}
                            </p>
                        );
                    })}
                </div>
            </Modal>

            {/* 离职确认弹窗 */}
            <Modal
                open={resignConfirmVisible}
                title={t('delivery.personnel.message.resignationConfirmTitle')}
                okText={t('components.button.confirm')}
                cancelText={t('components.button.cancel')}
                onOk={handleResignConfirmOk}
                onCancel={handleResignConfirmCancel}
            >
                <p>{t('delivery.personnel.message.resignationConfirmContent')}</p>
            </Modal>

            {/* 操作记录弹窗 */}
            <OperationLogModal
                visible={operationLogVisible}
                accountId={operationLogAccountId}
                mode={mode}
                warehouseId={currentWarehouseId ? parseInt(currentWarehouseId) : null}
                onClose={() => {
                    setOperationLogVisible(false);
                    setOperationLogAccountId(null);
                }}
            />

            {/* 批量上传弹窗 */}
            <AccountDeliveryDriverBatchCreateModal
                visible={batchUploadVisible}
                onCancel={() => setBatchUploadVisible(false)}
                onSuccess={handleBatchUploadSuccess}
                mode={mode}
            />

            {/* Driver download modal */}
            <DriverDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
                downloadPagePath="/central/download"
            />

            {/* 封禁司机弹窗 */}
            <DriverBlacklistModal
                visible={banDriverVisible}
                onCancel={() => setBanDriverVisible(false)}
                onSuccess={() => {
                    setBanDriverVisible(false);
                    handleRefetch();
                }}
            />

            {/* 司机黑名单列表 */}
            <DriverBlacklistListModal
                visible={blacklistListVisible}
                onClose={() => setBlacklistListVisible(false)}
            />
        </div>
    );
};

const AccountsPage: React.FC<{ mode?: AccountsPageMode }> = ({ mode }) => {
    return (
        <PageBody mode={mode} />
    );
}

export default AccountsPage;
